/**
 * Kopfgeld-Anzeige (NT-KOPFGELD-01) — reine Funktionen, von Rangliste und Steckbrief genutzt.
 * Kopfgeld ist eine Punktzahl, kein Geld: nie mit Währungszeichen anzeigen (K5).
 * Schwerpunkt-Keys kommen ASCII vom Backend (bounty.constants.ts), Labels mit Umlauten hier.
 */

export type BountyFocusKey = 'HAENDLER' | 'GAERTNER' | 'KOCH' | 'REVIERBOSS' | 'LOGISTIKER' | 'ALLROUNDER';

const FOCUS_LABELS: Record<BountyFocusKey, string> = {
  HAENDLER: 'HÄNDLER',
  GAERTNER: 'GÄRTNER',
  KOCH: 'KOCH',
  REVIERBOSS: 'REVIERBOSS',
  LOGISTIKER: 'LOGISTIKER',
  ALLROUNDER: 'ALLROUNDER',
};

/** Kurzform für schmale Bildschirme (375 px). */
const FOCUS_SHORT_LABELS: Record<BountyFocusKey, string> = {
  HAENDLER: 'HÄNDL.',
  GAERTNER: 'GÄRTN.',
  KOCH: 'KOCH',
  REVIERBOSS: 'REVIERB.',
  LOGISTIKER: 'LOGIST.',
  ALLROUNDER: 'ALLR.',
};

export const UNBESCHRIEBENES_BLATT = 'UNBESCHRIEBENES BLATT';

function isFocusKey(value: string): value is BountyFocusKey {
  return value in FOCUS_LABELS;
}

export function focusLabel(focus: string | null): string {
  return focus !== null && isFocusKey(focus) ? FOCUS_LABELS[focus] : '—';
}

export function focusShortLabel(focus: string | null): string {
  return focus !== null && isFocusKey(focus) ? FOCUS_SHORT_LABELS[focus] : '—';
}

/** Deutsches Tausendertrennzeichen, keine Nachkommastellen: 8420 → "8.420". */
export function formatBounty(value: number): string {
  return Math.round(value).toLocaleString('de-DE');
}

const SPARK_CHARS = ['▁', '▂', '▃', '▄', '▅', '▆', '▇', '█'] as const;

/**
 * Sparkline aus Unicode-Blockzeichen, ein Zeichen pro Wert. Skaliert auf das Maximum; 0 → "▁".
 */
export function renderSparkline(values: readonly number[]): string {
  const max = Math.max(0, ...values);
  return values
    .map((v) => {
      if (max <= 0 || v <= 0) return SPARK_CHARS[0];
      const idx = Math.min(SPARK_CHARS.length - 1, Math.round((v / max) * (SPARK_CHARS.length - 1)));
      return SPARK_CHARS[idx];
    })
    .join('');
}

/** Anteile in ganzen Prozent; Summe 0 → alle 0. */
export function bountyShares(components: { umsatz: number; produktion: number; revier: number; logistik: number }): {
  umsatz: number; produktion: number; revier: number; logistik: number;
} {
  const total = components.umsatz + components.produktion + components.revier + components.logistik;
  if (total <= 0) return { umsatz: 0, produktion: 0, revier: 0, logistik: 0 };
  const pct = (v: number): number => Math.round((v / total) * 100);
  return {
    umsatz: pct(components.umsatz),
    produktion: pct(components.produktion),
    revier: pct(components.revier),
    logistik: pct(components.logistik),
  };
}
